import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../services/supabase';
import { refreshBadges } from './useBadgeCounts';
import { lookupImpersonation, useStaffViewing } from './staffViewing';
import { sanitizeStorageSegment } from '../../utils/storagePath';

/**
 * "From ShoutOut" — announcements, direct messages and support threads between
 * ShoutOut and this creator (the Creator Inbox; tables live in the frontend
 * repo's migrations 20260929160732 onwards).
 *
 * Everything is gated server-side by platform_settings.creator_inbox_mode:
 * until it is 'all', a creator's reads come back empty and replies are refused,
 * so this hook simply reports `enabled: false` and the section never renders.
 *
 * READ-ONLY FOR STAFF. When staff are looking at the workspace (useStaffViewing)
 * nothing is marked read and replying is off — the creator's unread state must
 * only ever move because the creator looked. The database refuses both for a
 * recorded view-as session anyway; this keeps the other entry path honest too.
 */

export type ShoutOutThreadKind = 'announcement' | 'direct' | 'support';

export type ShoutOutThread = {
  thread_id: string;
  kind: ShoutOutThreadKind;
  subject: string | null;
  /** 'past' = history closed without a staff reply; shown as a past conversation. */
  status: 'open' | 'resolved' | 'past';
  source: 'staff' | 'ai_handoff' | 'migrated_chat';
  created_at: string;
  last_message_at: string;
  last_message_role: 'staff' | 'creator' | 'ai' | 'system' | null;
  last_message_preview: string | null;
  unread_count: number;
};

export type ShoutOutAttachment =
  | { type: 'image' | 'gif'; url: string }
  /** A "Copy my link" button under an automated message (Get to 30, Oct 2026). Sent by the server only. */
  | { type: 'copy_link'; url: string }
  /** A button into the dashboard under an automated message ("Try this next", Oct 2026). Sent by the server only. */
  | { type: 'dashboard_link'; label: string; path: string; item?: string }
  /** Accept / Decline under a collab request (creator directory, Oct 2026). Sent by the server only. */
  | { type: 'collab_request'; request_id: string }
  /** Under a "you're connected" message: the other creator (creator directory, Oct 2026). Sent by the server only. */
  | { type: 'collab_contact'; talent_id: string }
  /** "I'm in" / "Not this time" under a sponsorship offer (P1 campaigns, Oct 2026). Sent by the server only. */
  | { type: 'campaign_offer'; participant_id: string; campaign_id: string };

/** What a creator can attach and see as a picture. */
export type ShoutOutImage = Extract<ShoutOutAttachment, { type: 'image' | 'gif' }>;

/** Only paths inside the dashboard, with a plain query string: never another site. */
export const isDashboardPath = (path: unknown): path is string =>
  typeof path === 'string' && /^\/dashboard(\/[A-Za-z0-9_-]+)*(\?[A-Za-z0-9_=&-]*)?$/.test(path);

export const isImageAttachment = (a: ShoutOutAttachment): a is ShoutOutImage => a.type === 'image' || a.type === 'gif';

export type ShoutOutMessage = {
  message_id: string;
  sender_role: 'staff' | 'creator' | 'ai' | 'system';
  sender_name: string;
  is_mine: boolean;
  body: string;
  attachments: ShoutOutAttachment[];
  created_at: string;
  /** Set when ShoutOut staff changed the text after sending. */
  edited_at?: string | null;
};

const POLL_MS = 60_000;

/** Tells every mounted ShoutOut inbox to re-read (e.g. a thread was just created). */
export const SHOUTOUT_INBOX_REFRESH_EVENT = 'shoutout-inbox:refresh';

const IMAGE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

/** Upload into the creator-inbox bucket, under the signed-in user's own folder. */
export async function uploadShoutOutImage(file: File): Promise<ShoutOutImage> {
  const ext = IMAGE_EXTENSIONS[file.type];
  if (!ext) throw new Error('Only JPG, PNG, GIF or WebP images can be attached.');
  if (file.size > 5 * 1024 * 1024) throw new Error('Images must be under 5MB.');
  const { data: auth } = await supabase.auth.getSession();
  const uid = auth.session?.user?.id;
  if (!uid) throw new Error('You are signed out. Sign in again to attach images.');
  // uid comes from the session and ext from the allowlist above; the segment
  // sanitizer keeps the key inside the user's own folder regardless.
  const path = `${sanitizeStorageSegment(uid, 'user')}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from('creator-inbox')
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from('creator-inbox').getPublicUrl(path);
  return { type: file.type === 'image/gif' ? 'gif' : 'image', url: data.publicUrl };
}

export function useShoutOutInbox(ownerUserId: string | null | undefined) {
  const staff = useStaffViewing();
  const [enabled, setEnabled] = useState(false);
  const [threads, setThreads] = useState<ShoutOutThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, ShoutOutMessage[]>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);
  /** Bumped on every successful list load, so open threads re-read their messages
   *  and pick up staff edits and unsends, not just new messages. */
  const [refreshedAt, setRefreshedAt] = useState(0);
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const readOnly = staff.viewing;

  const load = useCallback(
    async (quiet = false) => {
      if (!ownerUserId) return;
      if (!quiet) setLoading(true);
      try {
        // The inbox belongs to the signed-in account. When the session is not
        // this page's owner (staff opening an unclaimed page with their own
        // session), it would show STAFF's threads here — so show nothing.
        const { data: auth } = await supabase.auth.getSession();
        if (auth.session?.user?.id !== ownerUserId) {
          if (mountedRef.current) setEnabled(false);
          return;
        }
        const { data: on, error: onError } = await supabase.rpc('creator_inbox_enabled');
        if (onError) throw onError;
        if (!mountedRef.current) return;
        setEnabled(!!on);
        if (!on) return;

        const { data, error: listError } = await supabase.rpc('creator_inbox_threads');
        if (listError) throw listError;
        if (!mountedRef.current) return;
        setThreads((data ?? []) as ShoutOutThread[]);
        setRefreshedAt(Date.now());
        setError(null);
      } catch (e: any) {
        if (mountedRef.current && !quiet) setError(e?.message || 'Could not load messages from ShoutOut.');
      } finally {
        if (mountedRef.current && !quiet) setLoading(false);
      }
    },
    [ownerUserId]
  );

  useEffect(() => {
    void load();
    const tick = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    const refresh = () => void load(true);
    const timer = window.setInterval(tick, POLL_MS);
    window.addEventListener('focus', tick);
    window.addEventListener(SHOUTOUT_INBOX_REFRESH_EVENT, refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', tick);
      window.removeEventListener(SHOUTOUT_INBOX_REFRESH_EVENT, refresh);
    };
  }, [load]);

  // Latest request per thread: only its answer is applied, so a slow, older
  // response can't bring back a message staff have since edited or unsent.
  const requestSeqRef = useRef<Record<string, number>>({});

  /** 'stale' when a newer request for the thread started meanwhile (ignored). */
  const loadMessages = useCallback(async (threadId: string): Promise<'loaded' | 'stale'> => {
    const seq = (requestSeqRef.current[threadId] ?? 0) + 1;
    requestSeqRef.current[threadId] = seq;
    const { data, error: msgError } = await supabase.rpc('creator_thread_messages', {
      p_thread_id: threadId,
    });
    if (requestSeqRef.current[threadId] !== seq) return 'stale';
    if (msgError) throw msgError;
    if (mountedRef.current) {
      setMessages((prev) => ({ ...prev, [threadId]: (data ?? []) as ShoutOutMessage[] }));
    }
    return 'loaded';
  }, []);

  /** Open a thread: load it and, for the creator themselves, mark it read. */
  const openThread = useCallback(
    async (threadId: string) => {
      try {
        // A superseded load proves nothing; the newer call decides about read.
        if ((await loadMessages(threadId)) === 'stale') return;
      } catch (e: any) {
        setError(e?.message || 'Could not open that conversation.');
        return;
      }
      const thread = threads.find((t) => t.thread_id === threadId);
      if (readOnly || !thread || thread.unread_count === 0) return;
      // Opened before the view-as lookup answered: ask it now rather than guess.
      if (!staff.resolved && (await lookupImpersonation())) return;
      setThreads((prev) => prev.map((t) => (t.thread_id === threadId ? { ...t, unread_count: 0 } : t)));
      const { error: readError } = await supabase.rpc('creator_mark_thread_read', { p_thread_id: threadId });
      if (!readError) refreshBadges();
    },
    [loadMessages, threads, readOnly, staff.resolved]
  );

  const reply = useCallback(
    async (threadId: string, body: string, attachments: ShoutOutAttachment[] = []): Promise<boolean> => {
      if (readOnly) return false;
      setSendingId(threadId);
      try {
        const { error: replyError } = await supabase.rpc('creator_reply', {
          p_thread_id: threadId,
          p_body: body,
          p_attachments: attachments,
        });
        if (replyError) throw replyError;
        await Promise.all([loadMessages(threadId), load(true)]);
        refreshBadges();
        return true;
      } catch (e: any) {
        setError(e?.message || 'Could not send your reply.');
        return false;
      } finally {
        if (mountedRef.current) setSendingId(null);
      }
    },
    [readOnly, loadMessages, load]
  );

  const unreadThreads = useMemo(() => threads.filter((t) => t.unread_count > 0).length, [threads]);

  return {
    enabled,
    loading,
    error,
    setError,
    threads,
    unreadThreads,
    messages,
    refreshedAt,
    openThread,
    reply,
    sendingId,
    readOnly,
    viewingAs: staff.viewing ? staff.adminName : null,
    refetch: load,
  };
}

export type ShoutOutInbox = ReturnType<typeof useShoutOutInbox>;
